import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { startTransition, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ChevronDownIcon from 'app/components/Icons/ChevronDownIcon';
import LogoutIcon from 'app/components/Icons/LogoutIcon';

import {
  ACTIVE_PATH_PATTERNS,
  getDefaultQueryParams,
  getSubMenuItems,
  matchesActivePattern,
  SidebarMenuItem,
  SidebarSubMenuItem,
} from '../model/menu';
import useSidebarMenu from '../model/useSidebarMenu';
import {
  activeMenuItem,
  bottomSection,
  chevronExpanded,
  chevronIcon,
  countBadge,
  countBadgeText,
  disabledMenuItem,
  divider,
  menuBox,
  menuIcon,
  menuItem,
  menuItemHover,
  menuLabel,
  sidebarMenuBox,
  submenuItemHover,
  submenuLabel,
  submenuLabelActive,
  submenuList,
  subtitleBadge,
  subtitleBadgeText,
} from './SidebarMenu.sx';

// Hint shown when hovering a disabled (guest) menu row.
const DEACTIVE_TOOLTIP = 'Zəhmət olmasa, ilk öncə hesab açma prosesini tamamlayın.';

export interface SideBarMenuProps {
  // Menu from get_header_menu (registered) or the guest menu (unregistered).
  menu: SidebarMenuItem[];
  setOpen?: (state: boolean) => void;
  title?: string;
  sign?: string;
}

const SidebarMenu = (props: SideBarMenuProps) => {
  const { setOpen } = props;
  const { t, isOpenLogoutModal, closeLogOutModal, openLogoutModal, handleLogout } =
    useSidebarMenu();

  const menu = props.menu ?? [];

  const navigate = useNavigate();
  const location = useLocation();

  const isCurrentPath = (path?: string) => {
    if (!path) return false;

    const [pathname, query] = path.split('?');
    const pathnameMatches =
      location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);

    if (!pathnameMatches) return false;
    if (!query) return true;

    // Items that differ only by query string (e.g. transfer-page?type=...) must
    // also match the query params of the current location, falling back to the
    // route's defaults for params the URL omits.
    const target = new URLSearchParams(query);
    const current = new URLSearchParams(location.search);
    const defaults = getDefaultQueryParams(location.pathname);

    return [...target.entries()].every(
      ([key, value]) => (current.get(key) ?? defaults[key]) === value
    );
  };

  // A sub-item is active when the current route matches its special active-path
  // patterns (keyed by id, since the API omits them) or, by default, its own
  // `path_front`.
  const isSubItemActive = (sub: SidebarSubMenuItem) => {
    const patterns = ACTIVE_PATH_PATTERNS[sub.id];

    if (patterns) {
      return patterns.some(pattern => matchesActivePattern(pattern, location.pathname));
    }

    return isCurrentPath(sub.path_front);
  };

  const currentMenuId = useMemo(() => {
    return menu
      .filter(item => Boolean(item.path_front) && !item.deactive)
      .map(item => ({ id: item.id, path: item.path_front as string }))
      .filter(item => isCurrentPath(item.path))
      .sort((left, right) => right.path.length - left.path.length)[0]?.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menu]);

  // Keep the accordion that contains the current route expanded by default.
  const [expandedIds, setExpandedIds] = useState<string[]>(() =>
    menu
      .filter(item => !item.deactive && getSubMenuItems(item)?.some(isSubItemActive))
      .map(item => item.id)
  );

  const toggleExpand = (id: string) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  const handleNavigation = (path: string) => {
    startTransition(() => {
      navigate(path);
    });
    setOpen?.(false);
  };

  const handleItemClick = (item: SidebarMenuItem) => {
    // Disabled (guest) rows are display-only.
    if (item.deactive) return;

    if (getSubMenuItems(item)) {
      toggleExpand(item.id);

      return;
    }

    const base = item.path_front as string;
    // The registered accounts page needs a ?type=accounts tab; skip it when the
    // path already carries a query (e.g. the unregistered guest review route).
    const path =
      item.id === 'account_list' && base.endsWith('/accounts-card')
        ? `${base}?type=accounts`
        : base;

    handleNavigation(path);
  };

  return (
    <Box sx={sidebarMenuBox}>
      <Box sx={menuBox}>
        {menu.map(item => {
          const subItems = getSubMenuItems(item);
          const isExpanded = expandedIds.includes(item.id);
          const isDeactive = Boolean(item.deactive);
          const isHighlighted =
            !isDeactive && (subItems ? subItems.some(isSubItemActive) : item.id === currentMenuId);

          return (
            <Box key={item.id}>
              {/* Disabled (guest) rows show a hint tooltip; enabled rows pass an
                  empty title so no tooltip is rendered. */}
              <Tooltip title={isDeactive ? DEACTIVE_TOOLTIP : ''}>
                <Box
                  sx={
                    isDeactive
                      ? { ...menuItem, ...disabledMenuItem }
                      : isHighlighted
                        ? { ...menuItem, ...activeMenuItem }
                        : { ...menuItem, ...menuItemHover }
                  }
                  onClick={() => handleItemClick(item)}
                >
                  {item.icon && <img src={item.icon} alt={item.title} style={menuIcon} />}
                  <Typography sx={menuLabel}>{item.title}</Typography>

                  {item.count && (
                    <Box sx={countBadge}>
                      <Typography sx={countBadgeText}>{item.count}</Typography>
                    </Box>
                  )}

                  {item.subtitle && (
                    <Box sx={subtitleBadge}>
                      <Typography sx={subtitleBadgeText}>{item.subtitle}</Typography>
                    </Box>
                  )}

                  {subItems && (
                    <ChevronDownIcon
                      sx={isExpanded ? { ...chevronIcon, ...chevronExpanded } : chevronIcon}
                    />
                  )}
                </Box>
              </Tooltip>

              {subItems && (
                <Collapse in={isExpanded} timeout={250} unmountOnExit>
                  <Box sx={submenuList}>
                    {subItems.map(sub => {
                      const isSubActive = isSubItemActive(sub);

                      return (
                        <Box
                          key={sub.id}
                          sx={{ ...menuItem, ...submenuItemHover }}
                          onClick={() => handleNavigation(sub.path_front)}
                        >
                          {sub.icon && <img src={sub.icon} alt={sub.title} style={menuIcon} />}
                          <Typography
                            sx={
                              isSubActive
                                ? { ...submenuLabel, ...submenuLabelActive }
                                : submenuLabel
                            }
                          >
                            {sub.title}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={bottomSection}>
        <Divider sx={divider} />
        <Box sx={{ ...menuItem, ...menuItemHover }} onClick={openLogoutModal}>
          <LogoutIcon color={'primary'} sx={{ ...menuIcon, fill: 'transparent' }} />
          <Typography sx={menuLabel}>{t('homePage.logout')}</Typography>
        </Box>
      </Box>

      <Dialog open={isOpenLogoutModal} onClose={closeLogOutModal}>
        <DialogContent sx={{ p: '40px 36px' }}>
          <Box sx={{ maxWidth: '70%' }}>
            <Typography variant={'h1'}>{t('homePage.logoutQuestion')}</Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            p: 4,
            gap: 1,
          }}
        >
          <Button color={'error'} onClick={handleLogout}>
            {t('homePage.logout')}
          </Button>
          <Button color={'secondary'} onClick={closeLogOutModal}>
            {t('commonActions.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SidebarMenu;
